/**
 * Seller "Payout Paused" panel.
 *
 * Renders below a seller's order row whenever the order has (or recently had)
 * a dispute. All eligibility flows from data already selected server-side —
 * the `disputes` join is RLS-scoped to participants + admin, so unrelated
 * users never see this panel or its underlying data.
 *
 * The panel deliberately renders no Stripe identifiers, transfer ids,
 * connected account ids or internal payout metadata; only the payout release
 * date (already visible on the order row) and dispute status text.
 */
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertOctagon,
  CheckCircle2,
  Clock,
  FileUp,
  MessageSquare,
  ShieldAlert,
  XCircle,
  BanknoteArrowUp,
} from "lucide-react";

type DisputeSummary = {
  id: string;
  status: string;
  admin_resolution: string | null;
  created_at: string;
  updated_at: string | null;
  resolved_at: string | null;
};

type OrderForPanel = {
  status: string;
  has_active_dispute: boolean;
  payout_released_at: string | null;
  disputes?: DisputeSummary[] | null;
};

const ACTIVE = new Set(["open", "awaiting_seller", "awaiting_buyer", "under_review"]);

function pickDispute(order: OrderForPanel): DisputeSummary | null {
  const list = order.disputes ?? [];
  if (list.length === 0) return null;
  // Prefer active, else most recent
  const active = list.find((d) => ACTIVE.has(d.status));
  if (active) return active;
  return [...list].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null;
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return "";
  const d = new Date(v);
  return d.toLocaleString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

type TimelineItem = { key: string; label: string; at: string | null; done: boolean };

function buildTimeline(order: OrderForPanel, d: DisputeSummary): TimelineItem[] {
  const status = d.status;
  const reachedUnderReview = ["under_review", "resolved_seller", "resolved_buyer", "closed"].includes(status);
  const evidenceRequested = status === "awaiting_seller" || status === "awaiting_buyer" || reachedUnderReview;
  const evidenceSubmitted = reachedUnderReview;
  const resolvedSeller = status === "resolved_seller" || d.admin_resolution === "resolved_seller";
  const resolvedBuyer = status === "resolved_buyer" || d.admin_resolution === "resolved_buyer" || order.status === "refunded";
  const paidOut = !!order.payout_released_at && resolvedSeller;
  const refunded = order.status === "refunded" && resolvedBuyer;

  return [
    { key: "reported", label: "Buyer reported a problem", at: d.created_at, done: true },
    { key: "opened", label: "Dispute opened", at: d.created_at, done: true },
    { key: "evidence_requested", label: "Evidence requested", at: evidenceRequested ? d.updated_at : null, done: evidenceRequested },
    { key: "evidence_submitted", label: "Evidence submitted", at: evidenceSubmitted ? d.updated_at : null, done: evidenceSubmitted },
    { key: "under_review", label: "Under admin review", at: reachedUnderReview ? d.updated_at : null, done: reachedUnderReview },
    { key: "resolved_seller", label: "Resolved in seller favour", at: resolvedSeller ? d.resolved_at : null, done: resolvedSeller },
    { key: "resolved_buyer", label: "Resolved in buyer favour", at: resolvedBuyer ? d.resolved_at : null, done: resolvedBuyer },
    { key: "payout_resumed", label: "Payout resumed", at: paidOut ? order.payout_released_at : null, done: paidOut },
    { key: "refund_completed", label: "Refund completed", at: refunded ? d.resolved_at : null, done: refunded },
  ];
}

export function SellerPayoutPausedPanel({ order }: { order: OrderForPanel }) {
  const dispute = pickDispute(order);
  if (!dispute) return null;

  const active = ACTIVE.has(dispute.status) || order.has_active_dispute;
  const sellerActionRequired = dispute.status === "awaiting_seller";
  const resolvedSeller = dispute.status === "resolved_seller" || dispute.admin_resolution === "resolved_seller";
  const resolvedBuyer = dispute.status === "resolved_buyer" || dispute.admin_resolution === "resolved_buyer";
  const closedNoAction = dispute.status === "closed" && !resolvedSeller && !resolvedBuyer;
  const paidOut = resolvedSeller && !!order.payout_released_at;
  const refunded = resolvedBuyer || order.status === "refunded";

  // Banner selection (only one)
  let banner: React.ReactNode = null;
  if (active) {
    banner = (
      <div
        role="status"
        aria-live="polite"
        className="w-full rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <AlertOctagon className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p className="font-display text-lg text-amber-200">⚠️ Payout Paused</p>
            <p className="text-sm text-amber-100/90">
              The buyer has reported a problem with this order. Your payout is temporarily on hold while this
              dispute is under review. Funds will not be released until the dispute has been resolved.
            </p>
          </div>
        </div>
      </div>
    );
  } else if (paidOut) {
    banner = (
      <div role="status" aria-live="polite"
        className="w-full rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <BanknoteArrowUp className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p className="font-display text-lg text-emerald-200">✅ Seller Paid</p>
            <p className="text-sm text-emerald-100/90">
              Funds released on {fmtDateTime(order.payout_released_at)}.
            </p>
          </div>
        </div>
      </div>
    );
  } else if (resolvedSeller) {
    banner = (
      <div role="status" aria-live="polite"
        className="w-full rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p className="font-display text-lg text-emerald-200">✅ Dispute resolved</p>
            <p className="text-sm text-emerald-100/90">Your payout is now being released.</p>
          </div>
        </div>
      </div>
    );
  } else if (refunded) {
    banner = (
      <div role="status" aria-live="polite"
        className="w-full rounded-xl border border-red-500/40 bg-red-500/10 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p className="font-display text-lg text-red-200">❌ Order Refunded</p>
            <p className="text-sm text-red-100/90">
              The buyer has been refunded. No payout will be sent for this order.
            </p>
          </div>
        </div>
      </div>
    );
  } else if (closedNoAction) {
    banner = (
      <div role="status" aria-live="polite"
        className="w-full rounded-xl border border-border/60 bg-surface-2/50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p className="font-display text-lg">Dispute closed</p>
            <p className="text-sm text-muted-foreground">
              The dispute was closed with no financial action. Standard payout timing resumes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const timeline = buildTimeline(order, dispute);

  return (
    <div className="space-y-4">
      {banner}

      {/* Status row: payout badge + reason + actions */}
      <div className="rounded-xl border border-border/60 bg-card p-4 sm:p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {active ? (
              <Badge variant="outline" className="bg-amber-500/15 text-amber-300 border-amber-500/40">
                🟡 Payout Paused
              </Badge>
            ) : paidOut ? (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                ✅ Seller Paid
              </Badge>
            ) : refunded ? (
              <Badge variant="outline" className="bg-red-500/15 text-red-300 border-red-500/40">
                ❌ Refunded
              </Badge>
            ) : resolvedSeller ? (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                ✅ Dispute Resolved
              </Badge>
            ) : (
              <Badge variant="outline">Dispute closed</Badge>
            )}
            {active && (
              <span className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">Reason:</span> Active dispute
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {active && sellerActionRequired && (
              <>
                <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900 w-full sm:w-auto">
                  <Link to="/disputes/$disputeId" params={{ disputeId: dispute.id }}>
                    <FileUp className="h-4 w-4 mr-1" aria-hidden /> Upload Evidence
                  </Link>
                </Button>
                <Button asChild size="sm" variant="outline" className="w-full sm:w-auto">
                  <Link to="/disputes/$disputeId" params={{ disputeId: dispute.id }}>
                    <MessageSquare className="h-4 w-4 mr-1" aria-hidden /> Reply to Buyer
                  </Link>
                </Button>
              </>
            )}
            {active && !sellerActionRequired && (
              <Button size="sm" disabled variant="outline" className="w-full sm:w-auto">
                <Clock className="h-4 w-4 mr-1" aria-hidden /> Waiting for Admin Review
              </Button>
            )}
            <Button asChild size="sm" variant="ghost" className="w-full sm:w-auto">
              <Link to="/disputes/$disputeId" params={{ disputeId: dispute.id }}>
                View Dispute
              </Link>
            </Button>
          </div>
        </div>

        {/* Timeline */}
        <ol className="grid gap-2 sm:grid-cols-2" aria-label="Dispute timeline">
          {timeline.map((t) => (
            <li key={t.key} className="flex items-start gap-2 text-sm">
              <span
                aria-hidden
                className={`mt-1 inline-block h-2 w-2 rounded-full shrink-0 ${
                  t.done ? "bg-gold" : "bg-muted-foreground/30"
                }`}
              />
              <div className="min-w-0">
                <span className={t.done ? "text-foreground" : "text-muted-foreground"}>
                  {t.done ? t.label : <span className="line-through/50">{t.label}</span>}
                </span>
                {t.at && (
                  <span className="block text-xs text-muted-foreground">{fmtDateTime(t.at)}</span>
                )}
              </div>
              <span className="sr-only">{t.done ? "completed" : "pending"}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export function orderShouldShowSellerPayoutPanel(order: OrderForPanel): boolean {
  if (order.has_active_dispute) return true;
  const list = order.disputes ?? [];
  if (list.length === 0) return false;
  // Show for orders with any dispute history (resolved/refunded/closed states).
  return true;
}
